/**
 * What the signed-in person may do (docs/admin-area.md — hub Plan 66).
 *
 * One call to `current_user_access()` at boot: the role and the parts the
 * database resolves for it. Roles follow the shared ladder
 * (SME-Connect bible/roles-standard.md): owner (everything, and alone gives
 * Admin), admin (everything, the admin area), manager and readonly (see every
 * Work record; change what their areas allow), facilitator (own classes).
 * A part — an area (Learner records, Attendance, Classes & set-up) or a module
 * (Learner pay, Staff HR) — is switched on per person; owner and admin have
 * every part. Every view reads `window.__access`, or asks hasPart / canChange.
 *
 * If the RPC is missing the role alone still answers — the app is then
 * exactly what it was before the admin area.
 */
import { supabase } from '../supabase.js';

/** @typedef {{ role: string, owner: boolean, admin: boolean, seeAll: boolean, hr: boolean, learnerPay: boolean, reports: boolean, parts: string[], mayGrantAdmin: boolean }} Access */

/** @returns {Promise<Access | null>} null = signed in but no role row. */
export async function loadAccess() {
  const { data, error } = await supabase.rpc('current_user_access');
  if (!error && data && data.role) {
    const parts = Array.isArray(data.parts) ? data.parts : [];
    return {
      role: data.role,
      owner: data.owner === true,
      admin: data.admin === true,
      seeAll: data.see_all === true,
      hr: data.hr === true,
      learnerPay: data.learner_pay === true,
      reports: parts.includes('reports'),
      parts,
      mayGrantAdmin: data.may_grant_admin === true,
    };
  }
  if (error) console.warn('[access] current_user_access unavailable — falling back to the role alone:', error.message);
  const { data: role } = await supabase.rpc('current_user_role');
  if (!role) return null;
  const owner = role === 'owner';
  const admin = owner || role === 'admin';
  return { role, owner, admin, seeAll: admin, hr: admin, learnerPay: admin, reports: admin, parts: admin ? ['reports'] : [], mayGrantAdmin: owner };
}

/** Is this part switched on for the signed-in person? (Owner and admin: always.) */
export function hasPart(key) {
  return window.__access?.parts?.includes(key) === true;
}

/**
 * May the signed-in person change records in a Work area on the page in front
 * of them? A facilitator changes learners and attendance in their own classes
 * (the database scopes it); everyone else needs the area switched on.
 */
export function canChange(area) {
  if (window.__access?.role === 'facilitator') return area === 'learners' || area === 'attendance';
  return hasPart(area);
}

/** The views that live on the Admin side of the rail. */
export const ADMIN_VIEWS = ['admin', 'admin-people', 'admin-audit', 'learner-payslips', 'hr-employees', 'hr-payslips', 'reports'];

export function isAdminView(name) {
  return ADMIN_VIEWS.includes(name);
}

/** The admin areas this person may open, in rail order, for the admin home. */
export function adminAreasFor(access) {
  if (!access?.admin) return [];
  const areas = [
    { view: 'admin-people', group: 'Admin', label: 'People & rights', blurb: 'Who can sign in, their role, and which parts of the dashboard they may use.' },
    { view: 'admin-audit',  group: 'Admin', label: 'Audit log',       blurb: 'Who changed what, and when — learners, classes, staff, payslips, access.' },
  ];
  if (access.reports) {
    areas.push(
      { view: 'reports', group: 'Management', label: 'Reports', blurb: 'Paymaster status reports: generate each section, check, approve, compile the form with graphs.' },
    );
  }
  if (access.learnerPay) {
    areas.push(
      { view: 'learner-payslips', group: 'Learner pay', label: 'Payslips & documents', blurb: 'Monthly stipend payslips and UIF documents: download or send on WhatsApp; document settings per cohort.' },
    );
  }
  if (access.hr) {
    areas.push(
      { view: 'hr-employees', group: 'HR', label: 'Employees', blurb: 'Londmarx’s own staff: contracts, pay and bank details.' },
      { view: 'hr-payslips',  group: 'HR', label: 'Staff payslips', blurb: 'Monthly payslips for staff: draft, final, paid, PDFs.' },
    );
  }
  return areas;
}
